import {
    Sheet,
    SheetContent,
    SheetFooter,
    SheetHeader,
    SheetTitle,
    SheetTrigger,
} from "@/components/ui/sheet"
import {toast} from "sonner"
import useJob from "@/query/workflowJob/useJob"
import {useState} from "react"
import type {Job} from "@/model/Job"
import {Button} from "@/components/ui/button"
import useInterruptJob from "@/query/workflowJob/useInterruptJob"
import {Label} from "@/components/ui/label"
import {Input} from "@/components/ui/input"
import {Textarea} from "@/components/ui/textarea"
import {formatJobResult} from "@/view/workflowJob/helper/formatJobResult"
import JobStatusBadge from "@/view/workflowJob/components/JobStatusBadge"
import JobProgressBar from "@/view/workflowJob/components/JobProgressBar"
import {useNavigate} from "react-router-dom"
import {Loader2, PanelRightOpen} from "lucide-react"
import {cn} from "@/lib/utils"

interface JobDetailSheetProps {
    job: Job
}

const JobDetailSheet = ({job}: JobDetailSheetProps) => {
    const navigate = useNavigate()
    const [isOpen, setIsOpen] = useState(false)

    const {data: jobDetail, isFetching: isJobDetailFetching} = useJob(job._id, isOpen)
    const displayJob = jobDetail ?? job

    const {mutateAsync: interruptJob, isLoading: isInterrupting} = useInterruptJob(job._id)

    const handleInterruptClick = () => {
        toast.promise(interruptJob(), {
            loading: "Interrupting job...",
            success: "Job interrupted",
            error: (error) => error.response?.data ?? "Failed to interrupt job",
        })
    }

    const handleViewResultsClick = () => {
        navigate(`/workflow-service/jobs/${displayJob._id}`)
    }

    return (
        <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild>
                <Button type="button" variant="outline" size="sm" className="gap-1.5">
                    <PanelRightOpen className="h-4 w-4"/>
                    Detail
                </Button>
            </SheetTrigger>
            <SheetContent className="flex h-full w-[500px] flex-col gap-0 p-0 sm:max-w-[500px]">
                <SheetHeader className="flex flex-col gap-3 border-b p-5 pr-12">
                    <SheetTitle className="flex items-center gap-2">
                        <JobStatusBadge job={displayJob}/>
                        <span className="min-w-0 flex-1 truncate">
                            {displayJob.name ?? displayJob._id}
                        </span>
                        {isJobDetailFetching && !jobDetail && (
                            <Loader2 className="h-4 w-4 shrink-0 animate-spin text-muted-foreground"/>
                        )}
                    </SheetTitle>
                    {displayJob.status === "running" && (
                        <JobProgressBar progressRate={displayJob.progressRate}/>
                    )}
                </SheetHeader>

                <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-5">
                    <div className="flex flex-col gap-1.5">
                        <Label>Type</Label>
                        <Input
                            value={displayJob.type}
                            readOnly
                            className="border-transparent bg-muted focus-visible:ring-0"
                        />
                    </div>

                    {displayJob.defaultParams?.chatbot_name && (
                        <div className="flex flex-col gap-1.5">
                            <Label>Chatbot Name</Label>
                            <Input
                                value={displayJob.defaultParams.chatbot_name}
                                readOnly
                                className="border-transparent bg-muted focus-visible:ring-0"
                            />
                        </div>
                    )}

                    {displayJob.description && (
                        <div className="flex flex-col gap-1.5">
                            <Label>Description</Label>
                            <Input
                                value={displayJob.description}
                                readOnly
                                className="border-transparent bg-muted focus-visible:ring-0"
                            />
                        </div>
                    )}

                    <div className="flex min-h-0 flex-1 flex-col gap-1.5">
                        <Label>Result</Label>
                        <Textarea
                            value={formatJobResult(displayJob.result)}
                            readOnly
                            className={cn(
                                "min-h-[280px] flex-1 resize-none border-transparent bg-muted focus-visible:ring-0",
                                !displayJob.result && "italic text-muted-foreground"
                            )}
                            placeholder="No result yet."
                        />
                    </div>
                </div>

                <SheetFooter className="flex flex-row items-center justify-end gap-2 border-t p-5">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={handleViewResultsClick}
                    >
                        View Results
                    </Button>
                    {displayJob.status === "running" && (
                        <Button
                            type="button"
                            variant="destructive"
                            disabled={isInterrupting}
                            onClick={handleInterruptClick}
                        >
                            {isInterrupting ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin"/>
                                    Interrupting...
                                </>
                            ) : (
                                "Interrupt"
                            )}
                        </Button>
                    )}
                </SheetFooter>
            </SheetContent>
        </Sheet>
    )
}

export default JobDetailSheet
